<script setup lang="ts">
/**
 * InitiativeActions — botões de override R1-R6.
 *
 * HLOS-40 (HLOS-37.3) — Sprint 3 / AC4-S3 da REQ-006.
 * 3 botões aparecem QUANDO há iniciativa ativa (não silent):
 *  - Recusar (R3: ask → learner). CC-05: zero penalidade.
 *  - Modificar (R4: explain_reasoning → shared). Abre dialog.
 *  - Pausar (R5: pause → learner). Pausa o Companheiro inteiro.
 *
 * Constitucional:
 * - CC-05: zero streak counter, zero "X dias sem usar", zero shame.
 * - Princípio V (Aprendiz Decide): affordance explícita de recusa/modificação.
 * - CC-06 (sem dark patterns): copy neutra, sem "não perca" / "expira".
 * - Princípio XI (Companion ≠ LLM): ações não mencionam "AI"/"assistant".
 */
import { ref } from "vue";
import { useInitiative } from "../composables/useInitiative";
import { useCompanion } from "../../companion/composables/useCompanion";
import ModifyInitiativeDialog from "./ModifyInitiativeDialog.vue";

const initiative = useInitiative();
const companion = useCompanion();

const showModifyDialog = ref(false);

function onRefuse(): void {
  initiative.refuseInitiative();
}

function onModify(): void {
  showModifyDialog.value = true;
}

function onPause(): void {
  companion.pause();
}

function onResume(): void {
  companion.resume();
}

function onModifySubmit(userInput: string): void {
  initiative.modifyInitiative(userInput);
  showModifyDialog.value = false;
}

function onModifyCancel(): void {
  showModifyDialog.value = false;
}
</script>

<template>
  <div
    v-if="initiative.hasInitiative.value && companion.snapshot.value.state !== 'paused'"
    class="hlos-initiative-actions"
    data-testid="initiative-actions"
    role="group"
    aria-label="Ações sobre a iniciativa do Companheiro"
  >
    <button
      type="button"
      class="hlos-initiative-actions__btn hlos-initiative-actions__btn--refuse"
      data-testid="initiative-refuse"
      @click="onRefuse"
    >
      Recusar
    </button>
    <button
      type="button"
      class="hlos-initiative-actions__btn hlos-initiative-actions__btn--modify"
      data-testid="initiative-modify"
      @click="onModify"
    >
      Modificar
    </button>
    <button
      type="button"
      class="hlos-initiative-actions__btn hlos-initiative-actions__btn--pause"
      data-testid="initiative-pause"
      @click="onPause"
    >
      Pausar Companheiro
    </button>
  </div>

  <div
    v-else-if="companion.snapshot.value.state === 'paused'"
    class="hlos-initiative-actions hlos-initiative-actions--paused"
    data-testid="initiative-paused-banner"
    role="status"
    aria-live="polite"
  >
    <span>Companheiro pausado.</span>
    <button
      type="button"
      class="hlos-initiative-actions__btn hlos-initiative-actions__btn--resume"
      data-testid="initiative-resume"
      @click="onResume"
    >
      Retomar
    </button>
  </div>

  <Teleport v-if="showModifyDialog" to="body">
    <ModifyInitiativeDialog
      :open="showModifyDialog"
      @submit="onModifySubmit"
      @cancel="onModifyCancel"
    />
  </Teleport>
</template>

<style scoped>
.hlos-initiative-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0;
  padding: 0.5rem 0;
}
.hlos-initiative-actions--paused {
  background: color-mix(in srgb, currentColor 8%, transparent);
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  align-items: center;
}
.hlos-initiative-actions__btn {
  padding: 0.4rem 0.8rem;
  border: 1px solid color-mix(in srgb, currentColor 30%, transparent);
  background: transparent;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.9em;
  /* Hover discreto — sem dark pattern (CC-06) */
  transition: background 0.15s ease;
}
.hlos-initiative-actions__btn:hover {
  background: color-mix(in srgb, currentColor 8%, transparent);
}
.hlos-initiative-actions__btn:focus-visible {
  outline: 2px solid color-mix(in srgb, currentColor 60%, transparent);
  outline-offset: 2px;
}
.hlos-initiative-actions__btn--resume {
  margin-left: auto;
}
</style>
